const resources = {
  en: {
    legal: {
      cta: {
        contactSupport: 'Contact support',
      },
      contentTitle: 'Document content',
      contentDescription: 'Read the key points below, then use the related links and document details if you need the full legal context.',
      nav: {
        back: 'Back',
        backToApp: 'Back to app',
        backToLogin: 'Sign in',
      },
      links: {
        title: 'Related documents',
        about: 'About',
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
        refund: 'Refund Policy',
        contactSupport: 'Contact support',
      },
      meta: {
        title: 'Document details',
        productNameLabel: 'Product name',
        supportEmailLabel: 'Support page',
        effectiveDateLabel: 'Effective date',
        lastUpdatedLabel: 'Last updated',
      },
      terms: {
        title: 'Terms of Service',
        subtitle: 'Clear rules for using FinApp.',
        sections: {
          about: {
            title: '1. About the service',
            body: 'FinApp is a personal finance organization tool for accounts, transactions, planning, reminders, forecasts, and insights. It is an information and record-keeping service, not a financial service or financial institution. FinApp is not a bank, accountant, investment advisor, legal advisor, or tax advisor. By creating an account or using the service, you accept these Terms and the Privacy Policy.',
          },
          account: {
            title: '2. Your account',
            body: 'You are responsible for the accuracy of data you enter, for keeping access secure, and for reviewing outputs before relying on them. If you notice suspicious activity, contact support immediately.',
          },
          subscriptions: {
            title: '3. Subscriptions and billing',
            body: 'Paid plans renew on the billing cycle shown at checkout and are processed by Stripe. You can cancel through the app billing flow when available; access remains active until the end of the paid period. Refund requests are reviewed through support and are not guaranteed unless required by law.',
          },
          acceptableUse: {
            title: '4. Acceptable use',
            body: 'Do not abuse the service, attempt to access other users’ data, or disrupt other users. We may suspend or close accounts that violate these terms.',
          },
          privacy: {
            title: '5. Privacy',
            body: 'How we collect, use, export, delete, retain, and protect data is described in the Privacy Policy. Some security, audit, billing, anti-abuse, or backup records may be retained for legitimate operational or legal reasons.',
          },
          userControls: {
            title: '6. Data export and account deletion',
            body: 'You can request and download a copy of your account data from the app and can request account deletion. These tools have documented exclusions: secrets, internal security records, audit records, billing-provider records, anti-abuse records, and backups may be excluded or retained when necessary.',
          },
          liability: {
            title: '7. Informational use and liability',
            body: 'Forecasts, reminders, AI summaries, and insights are informational only. They may be incomplete or wrong if your data is incomplete or wrong. You remain responsible for financial, tax, legal, investment, and payment decisions.',
          },
          changes: {
            title: '8. Changes',
            body: 'We may update these terms as the product evolves. When changes are significant, we publish a new version and update the dates below.',
          },
          contact: {
            title: 'Contact support',
            body: 'If you have questions about the terms, use the support page:',
          },
        },
      },
      about: {
        title: 'About FinApp',
        subtitle: 'A focused workspace for mindful personal finance.',
        sections: {
          mission: {
            title: '1. What it is',
            body: 'FinApp is a personal finance workspace for tracking accounts, budgets, and cash flow with clear, manual input. It is built for people who want control and understanding rather than automated bank syncing.',
          },
          features: {
            title: '2. What it helps you do',
            body: 'Organize accounts, transactions, budgets, recurring payments, reminders, forecasts, goals, and insights. AI-assisted features are optional and are designed to summarize patterns or suggest next steps, not to make decisions for you.',
          },
          data: {
            title: '3. Your data',
            body: 'You decide what to enter, edit, export, or delete. We do not sell personal data. Payments are handled by Stripe, and product data is protected with access controls, audit logging, retention decisions, and privacy-conscious operational alerts.',
          },
          protection: {
            title: '4. Data protection built into the product',
            body: 'The current privacy/security baseline includes registration consent capture, data export, account deletion, admin audit logs, incident tracking, protected metrics, PII-safe alerts, and scheduled cleanup for approved operational records.',
          },
          notAdvice: {
            title: '5. What FinApp is not',
            body: 'FinApp is not a bank, accountant, investment advisor, legal advisor, or tax advisor. It does not guarantee financial outcomes and should not replace professional advice.',
          },
          contact: {
            title: 'Contact support',
            body: 'Questions about the app? Use the support page:',
          },
        },
      },
      privacy: {
        title: 'Privacy Policy',
        subtitle: 'A quick overview of what we collect and why.',
        sections: {
          dataWeCollect: {
            title: '1. Data we collect',
            body: 'We collect account profile data, contact email, authentication/security data, user settings, accounts, transactions, budgets, debts, liabilities, goals, recurring items, tasks, support requests, billing/subscription status, push subscription metadata, AI conversations/usage where used, and operational logs needed to run the service.',
            note: 'We do not collect payment card numbers directly. Card payments are processed by Stripe. Do not send passwords, card numbers, or sensitive secrets through support tickets.',
          },
          use: {
            title: '2. How we use data',
            body: 'We use data to provide account organization, balances, forecasts, reminders, budgeting, recurring tasks, insights, subscription access, support, security monitoring, auditability, backups, and service reliability.',
          },
          sharing: {
            title: '3. Data sharing',
            body: 'We do not sell personal data. We share data only with processors and infrastructure providers needed to operate the service.',
            note: 'Current processors can include Stripe for payments, an SMTP/email provider for messages, OpenAI for AI features you use, MongoDB hosting, Telegram for sanitized operator alerts, S3-compatible object storage for backups, browser push infrastructure, and CDN/proxy infrastructure.',
          },
          processors: {
            title: '4. Processors and external services',
            body: 'Each processor receives only the data needed for its purpose. Vendor contract and DPA status is tracked operationally and may require separate review outside the app.',
          },
          securityLogs: {
            title: '5. Security, audit, and operational logs',
            body: 'We maintain security, audit, billing, support, backup, worker, and operational records to protect the service and provide accountability. These records are not always visible in user-facing screens and may be retained separately from account content.',
          },
          retention: {
            title: '6. Retention',
            body: 'Retention periods are defined by data type. Some generated export artifacts expire quickly, while audit logs, security incident records, billing records, and backups may be retained longer for security, operational, legal, or business reasons. Backups remain until their lifecycle period expires.',
          },
          rights: {
            title: '7. Your controls',
            body: 'You can update your profile, request account deletion, and request and download a copy of your account data from the app. Some internal security, audit, billing, anti-abuse, or backup records may be excluded or retained when required. For privacy questions, contact support.',
          },
          contact: {
            title: 'Contact support',
            body: 'For privacy questions, use the support page:',
          },
        },
      },
      refund: {
        title: 'Refund and Cancellation Policy',
        subtitle: 'How cancellations and refunds work.',
        sections: {
          cancel: {
            title: '1. Subscription cancellation',
            body: 'Subscriptions renew automatically. You can cancel at any time via Settings → Billing → Cancel. Access remains through the end of the current paid period.',
          },
          refunds: {
            title: '2. Refund conditions',
            body: 'Refund requests are reviewed case by case through support. We do not promise automatic refunds or refunds for partial periods or unused time unless required by law. If a billing error occurred, contact support with the account email and billing date.',
          },
          trials: {
            title: '3. Trials and promos',
            body: 'If you had a free or promotional period, billing begins after the trial ends. Trial access time is not refundable.',
          },
          help: {
            title: '4. How to request help',
            body: 'Submit a support request with the account email, billing date, and a short description. Do not send payment card numbers, passwords, or sensitive secrets.',
          },
          contact: {
            title: 'Contact support',
            body: 'Reach us via the support page:',
          },
        },
      },
    },
  },
  uk: {
    legal: {
      cta: {
        contactSupport: 'Звернутися до підтримки',
      },
      contentTitle: 'Зміст документа',
      contentDescription: 'Ознайомтеся з основними пунктами нижче, а потім використайте пов’язані посилання та відомості про документ для повного юридичного контексту.',
      nav: {
        back: 'Назад',
        backToApp: 'Назад до застосунку',
        backToLogin: 'Увійти',
      },
      links: {
        title: 'Пов’язані документи',
        about: 'Про застосунок',
        terms: 'Умови використання',
        privacy: 'Політика конфіденційності',
        refund: 'Політика повернень',
        contactSupport: 'Зв’язатися з підтримкою',
      },
      meta: {
        title: 'Відомості про документ',
        productNameLabel: 'Назва продукту',
        supportEmailLabel: 'Сторінка підтримки',
        effectiveDateLabel: 'Дата набрання чинності',
        lastUpdatedLabel: 'Останнє оновлення',
      },
      terms: {
        title: 'Умови використання',
        subtitle: 'Зрозумілі правила користування FinApp.',
        sections: {
          about: {
            title: '1. Про сервіс',
            body: 'FinApp — це інструмент для організації особистих фінансів: рахунків, транзакцій, планування, нагадувань, прогнозів та інсайтів. Це інформаційний та обліковий сервіс, а не фінансова послуга чи фінансова установа. FinApp не є банком, бухгалтером, інвестиційним радником, юридичним радником або податковим радником. Створюючи акаунт або користуючись сервісом, ви приймаєте ці Умови та Політику конфіденційності.',
          },
          account: {
            title: '2. Ваш акаунт',
            body: 'Ви відповідаєте за точність даних, які вносите, за безпеку доступу та за перевірку результатів перед тим, як покладатися на них. Якщо помітите підозрілу активність, одразу зверніться до підтримки.',
          },
          subscriptions: {
            title: '3. Підписки та оплати',
            body: 'Платні плани поновлюються за циклом, указаним під час оплати, а платежі обробляє Stripe. Ви можете скасувати підписку через білінг-флоу в застосунку, якщо він доступний; доступ зберігається до кінця оплаченого періоду. Запити на повернення розглядаються через підтримку та не гарантуються, якщо цього не вимагає закон.',
          },
          acceptableUse: {
            title: '4. Допустиме використання',
            body: 'Не можна зловживати сервісом, намагатися отримати доступ до чужих даних або заважати іншим користувачам. У разі порушень ми можемо призупинити або закрити акаунт.',
          },
          privacy: {
            title: '5. Конфіденційність',
            body: 'Як ми збираємо, використовуємо, експортуємо, видаляємо, зберігаємо та захищаємо дані, описано в Політиці конфіденційності. Деякі записи безпеки, аудиту, білінгу, захисту від зловживань або резервні копії можуть зберігатися з легітимних операційних або юридичних причин.',
          },
          userControls: {
            title: '6. Експорт даних і видалення акаунта',
            body: 'Ви можете запросити й завантажити копію даних акаунта в застосунку та можете запросити видалення акаунта. Ці інструменти мають задокументовані винятки: секрети, внутрішні записи безпеки, аудиту, білінг-провайдера, захисту від зловживань і резервні копії можуть бути виключені або збережені, коли це необхідно.',
          },
          liability: {
            title: '7. Інформаційне використання та відповідальність',
            body: 'Прогнози, нагадування, AI-підсумки та інсайти мають лише інформаційний характер. Вони можуть бути неповними або помилковими, якщо ваші дані неповні або помилкові. Ви залишаєтесь відповідальними за фінансові, податкові, юридичні, інвестиційні та платіжні рішення.',
          },
          changes: {
            title: '8. Зміни',
            body: 'Ми можемо оновлювати умови в міру розвитку продукту. За суттєвих змін публікуємо нову версію та оновлюємо дати нижче.',
          },
          contact: {
            title: 'Звернутися до підтримки',
            body: 'Якщо у вас є питання щодо умов, скористайтеся сторінкою підтримки:',
          },
        },
      },
      about: {
        title: 'Про FinApp',
        subtitle: 'Зосереджений простір для усвідомлених особистих фінансів.',
        sections: {
          mission: {
            title: '1. Що це таке',
            body: 'FinApp — це робочий простір для ведення рахунків, бюджетів і грошового потоку з прозорим ручним введенням. Ми створили його для людей, яким потрібні контроль і розуміння без автоматичного банківського синку.',
          },
          features: {
            title: '2. Чим він допомагає',
            body: 'Організовуйте рахунки, транзакції, бюджети, регулярні платежі, нагадування, прогнози, цілі та інсайти. AI‑функції необов’язкові й призначені для підсумовування патернів або підказок щодо наступних кроків, а не для прийняття рішень замість вас.',
          },
          data: {
            title: '3. Ваші дані',
            body: 'Ви вирішуєте, що додавати, редагувати, експортувати чи видаляти. Ми не продаємо персональні дані. Платежі обробляє Stripe, а дані продукту захищаються контролями доступу, аудит-логами, рішеннями щодо строків зберігання та privacy-conscious операційними сповіщеннями.',
          },
          protection: {
            title: '4. Захист даних у продукті',
            body: 'Поточний privacy/security baseline включає фіксацію згоди під час реєстрації, експорт даних, видалення акаунта, адмінські аудит-логи, incident tracking, захищені метрики, PII-safe alerts і планове очищення погоджених операційних записів.',
          },
          notAdvice: {
            title: '5. Чим FinApp не є',
            body: 'FinApp не є банком, бухгалтером, інвестиційним радником, юридичним радником або податковим радником. Він не гарантує фінансових результатів і не замінює професійну консультацію.',
          },
          contact: {
            title: 'Звернутися до підтримки',
            body: 'Питання щодо застосунку? Скористайтеся сторінкою підтримки:',
          },
        },
      },
      privacy: {
        title: 'Політика конфіденційності',
        subtitle: 'Короткий огляд того, які дані ми збираємо і навіщо.',
        sections: {
          dataWeCollect: {
            title: '1. Які дані ми збираємо',
            body: 'Ми збираємо дані профілю акаунта, контактний email, дані автентифікації/безпеки, налаштування користувача, рахунки, транзакції, бюджети, борги, зобов’язання, цілі, регулярні записи, задачі, звернення в підтримку, статус білінгу/підписки, metadata push-підписок, AI-розмови/usage якщо ви користуєтесь AI-функціями, а також операційні логи, потрібні для роботи сервісу.',
            note: 'Ми не збираємо номери платіжних карт напряму. Платежі обробляє Stripe. Не надсилайте паролі, номери карт або чутливі секрети через звернення в підтримку.',
          },
          use: {
            title: '2. Як ми використовуємо дані',
            body: 'Ми використовуємо дані для організації рахунків, балансів, прогнозів, нагадувань, бюджетів, регулярних задач, інсайтів, доступу за підпискою, підтримки, security monitoring, auditability, резервного копіювання та стабільності сервісу.',
          },
          sharing: {
            title: '3. Передача даних',
            body: 'Ми не продаємо персональні дані. Ми передаємо дані лише процесорам та інфраструктурним провайдерам, потрібним для роботи сервісу.',
            note: 'Поточні процесори можуть включати Stripe для платежів, SMTP/email-провайдера для повідомлень, OpenAI для AI-функцій, якими ви користуєтесь, MongoDB hosting, Telegram для sanitized operator alerts, S3-compatible object storage для резервних копій, browser push infrastructure та CDN/proxy infrastructure.',
          },
          processors: {
            title: '4. Процесори та зовнішні сервіси',
            body: 'Кожен процесор отримує лише дані, потрібні для своєї цілі. Contract/DPA status постачальників відстежується операційно та може потребувати окремої перевірки поза застосунком.',
          },
          securityLogs: {
            title: '5. Security, audit та операційні логи',
            body: 'Ми ведемо security, audit, billing, support, backup, worker та операційні записи, щоб захищати сервіс і забезпечувати accountability. Ці записи не завжди видимі в користувацькому інтерфейсі та можуть зберігатися окремо від контенту акаунта.',
          },
          retention: {
            title: '6. Строк зберігання',
            body: 'Строки зберігання визначаються за типом даних. Деякі згенеровані export artifacts швидко спливають, тоді як audit logs, security incident records, billing records і backups можуть зберігатися довше з причин безпеки, операційних, юридичних або бізнес-потреб. Резервні копії залишаються до завершення lifecycle period.',
          },
          rights: {
            title: '7. Ваш контроль',
            body: 'Ви можете оновлювати профіль, запитувати видалення акаунта, а також запросити й завантажити копію даних акаунта в застосунку. Деякі внутрішні записи безпеки, аудиту, білінгу, захисту від зловживань або резервні копії можуть бути виключені чи збережені, якщо це потрібно. Для питань конфіденційності зверніться до підтримки.',
          },
          contact: {
            title: 'Звернутися до підтримки',
            body: 'Для питань конфіденційності скористайтеся сторінкою підтримки:',
          },
        },
      },
      refund: {
        title: 'Політика повернень та скасувань',
        subtitle: 'Як працюють скасування підписки та повернення коштів.',
        sections: {
          cancel: {
            title: '1. Скасування підписки',
            body: 'Підписки поновлюються автоматично. Ви можете скасувати підписку в будь-який момент через Settings → Billing → Cancel. Доступ зберігається до кінця поточного оплаченого періоду.',
          },
          refunds: {
            title: '2. Умови повернення',
            body: 'Запити на повернення розглядаються індивідуально через підтримку. Ми не обіцяємо автоматичні повернення або повернення за неповний період чи невикористаний час, якщо цього не вимагає закон. Якщо сталася помилка списання, зверніться до підтримки з email акаунта та датою списання.',
          },
          trials: {
            title: '3. Пробний період і промо',
            body: 'Якщо у вас був безкоштовний або промо-доступ, списання починається після завершення пробного періоду. Час пробного доступу не підлягає поверненню.',
          },
          help: {
            title: '4. Як запросити допомогу',
            body: 'Надішліть запит через сторінку підтримки, вказавши email акаунта, дату списання та короткий опис. Не надсилайте номери платіжних карт, паролі або чутливі секрети.',
          },
          contact: {
            title: 'Звернутися до підтримки',
            body: 'Зв’яжіться з нами через сторінку підтримки:',
          },
        },
      },
    },
  },
};

export default resources;
